import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../config';
import { formatearFechaBD as formatFechaAR } from '../../utils/reciboUtils';

function ReportePorMes() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mesExportar, setMesExportar] = useState(0); // 0 = todos los meses
  const [reporte, setReporte] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    cargarReporte();
  }, [anio]);

  const cargarReporte = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/api/vacaciones/admin/reporte-por-mes?anio=${anio}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar el reporte');
      }

      const data = await response.json();
      setReporte(data.reporte || []);
    } catch (err) {
      console.error('Error cargando reporte:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Filtrar datos según el mes seleccionado
    const datosAExportar = mesExportar === 0 
      ? reporte 
      : reporte.filter(mes => mes.mes === mesExportar);

    // Hoja de resumen
    const resumenData = datosAExportar.map(mes => ({
      'Mes': mes.nombre_mes,
      'Empleados de Vacaciones': mes.total_empleados,
      'Total Días': mes.total_dias
    }));

    const wsResumen = XLSX.utils.json_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(workbook, wsResumen, 'Resumen');

    // Hoja de detalle
    const detalleData = [];
    datosAExportar.forEach(mes => {
      mes.vacaciones.forEach(vacacion => {
        detalleData.push({
          'Mes': mes.nombre_mes,
          'Legajo': vacacion.legajo || '',
          'Empleado': `${vacacion.apellido}, ${vacacion.nombre}`,
          'Fecha Inicio': formatFechaAR(vacacion.fecha_inicio),
          'Fecha Fin': formatFechaAR(vacacion.fecha_fin),
          'Días Solicitados': vacacion.dias_solicitados,
          'Días en el Mes': vacacion.dias_en_mes,
          'Estado': vacacion.estado === 'suspendido' ? 'Suspendida (días parciales)' : 'Aprobada',
          'Comentarios': vacacion.comentarios || ''
        });
      });
    });

    const wsDetalle = XLSX.utils.json_to_sheet(detalleData);
    XLSX.utils.book_append_sheet(workbook, wsDetalle, 'Detalle');

    // Nombre del archivo según el filtro
    const nombreArchivo = mesExportar === 0
      ? `Reporte_Vacaciones_${anio}.xlsx`
      : `Reporte_Vacaciones_${datosAExportar[0]?.nombre_mes}_${anio}.xlsx`;

    // Descargar archivo
    XLSX.writeFile(workbook, nombreArchivo);
  };

  const formatearFecha = (fecha) => formatFechaAR(fecha);

  // Generar opciones de años (5 años atrás y 2 adelante)
  const generarOpcionesAnios = () => {
    const anioActual = new Date().getFullYear();
    const anios = [];
    for (let i = anioActual - 5; i <= anioActual + 2; i++) {
      anios.push(i);
    }
    return anios;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              📊 Reporte de Vacaciones por Mes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Año</InputLabel>
              <Select
                value={anio}
                label="Año"
                onChange={(e) => setAnio(e.target.value)}
              >
                {generarOpcionesAnios().map(a => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Mes a Exportar</InputLabel>
              <Select
                value={mesExportar}
                label="Mes a Exportar"
                onChange={(e) => setMesExportar(e.target.value)}
              >
                <MenuItem value={0}>Todos los meses</MenuItem>
                <MenuItem value={1}>Enero</MenuItem>
                <MenuItem value={2}>Febrero</MenuItem>
                <MenuItem value={3}>Marzo</MenuItem>
                <MenuItem value={4}>Abril</MenuItem>
                <MenuItem value={5}>Mayo</MenuItem>
                <MenuItem value={6}>Junio</MenuItem>
                <MenuItem value={7}>Julio</MenuItem>
                <MenuItem value={8}>Agosto</MenuItem>
                <MenuItem value={9}>Septiembre</MenuItem>
                <MenuItem value={10}>Octubre</MenuItem>
                <MenuItem value={11}>Noviembre</MenuItem>
                <MenuItem value={12}>Diciembre</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={cargarReporte}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportarExcel}
              disabled={reporte.length === 0}
            >
              Exportar Excel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tarjetas de resumen */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {reporte.slice(0, 12).map(mes => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={mes.mes}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%',
                      bgcolor: mes.total_empleados > 0 ? 'background.paper' : 'grey.50',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {mes.nombre_mes}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Empleados de vacaciones
                        </Typography>
                        <Typography variant="h4" color="primary">
                          {mes.total_empleados}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total de días
                        </Typography>
                        <Typography variant="h5">
                          {mes.total_dias}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Detalle por mes */}
            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
              Detalle por Mes
            </Typography>

            {reporte.map(mes => (
              mes.vacaciones.length > 0 && (
                <Accordion key={mes.mes} defaultExpanded={mes.mes === new Date().getMonth() + 1}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography variant="h6">
                        {mes.nombre_mes} {anio}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip 
                          label={`${mes.total_empleados} empleado${mes.total_empleados !== 1 ? 's' : ''}`} 
                          color="primary" 
                          size="small"
                        />
                        <Chip 
                          label={`${mes.total_dias} día${mes.total_dias !== 1 ? 's' : ''}`} 
                          color="secondary" 
                          size="small"
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Legajo</strong></TableCell>
                            <TableCell><strong>Empleado</strong></TableCell>
                            <TableCell><strong>Fecha Inicio</strong></TableCell>
                            <TableCell><strong>Fecha Fin</strong></TableCell>
                            <TableCell align="center"><strong>Días Total</strong></TableCell>
                            <TableCell align="center"><strong>Días en {mes.nombre_mes}</strong></TableCell>
                            <TableCell align="center"><strong>Estado</strong></TableCell>
                            <TableCell><strong>Comentarios</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {mes.vacaciones.map((vacacion, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{vacacion.legajo || '-'}</TableCell>
                              <TableCell>{vacacion.apellido}, {vacacion.nombre}</TableCell>
                              <TableCell>{formatearFecha(vacacion.fecha_inicio)}</TableCell>
                              <TableCell>{formatearFecha(vacacion.fecha_fin)}</TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={vacacion.dias_solicitados} 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={vacacion.dias_en_mes}
                                  size="small"
                                  color="secondary"
                                />
                              </TableCell>
                              <TableCell align="center">
                                {vacacion.estado === 'suspendido' ? (
                                  <Tooltip title="Se cortó antes de terminar; los días mostrados son solo los que llegó a gozar">
                                    <Chip label="⏸ Suspendida" size="small" color="warning" variant="outlined" />
                                  </Tooltip>
                                ) : (
                                  <Chip label="Aprobada" size="small" color="success" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={vacacion.comentarios || ''}>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                    {vacacion.comentarios || '-'}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )
            ))}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default ReportePorMes;
